import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "@/lib/gmaps";
import { MapPin } from "lucide-react";

/**
 * Embedded Google Map with a directions polyline from origin (user GPS) to destination.
 * Falls back to a clean placeholder when the Maps API key isn't configured.
 */
export default function GoogleMapDirections({ origin, destination, className = "", testid = "google-map" }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const rendererRef = useRef(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        if (!destination?.lat || !destination?.lng) return;
        loadGoogleMaps().then((google) => {
            if (cancelled || !containerRef.current) return;
            if (!mapRef.current) {
                mapRef.current = new google.maps.Map(containerRef.current, {
                    center: { lat: destination.lat, lng: destination.lng },
                    zoom: 13,
                    disableDefaultUI: true,
                    zoomControl: true,
                    styles: [
                        { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
                    ],
                });
                rendererRef.current = new google.maps.DirectionsRenderer({
                    map: mapRef.current,
                    suppressMarkers: false,
                    polylineOptions: { strokeColor: "#0066ff", strokeWeight: 5, strokeOpacity: 0.85 },
                });
            }
            if (origin?.lat && origin?.lng) {
                const ds = new google.maps.DirectionsService();
                ds.route(
                    {
                        origin: { lat: origin.lat, lng: origin.lng },
                        destination: { lat: destination.lat, lng: destination.lng },
                        travelMode: google.maps.TravelMode.DRIVING,
                    },
                    (result, status) => {
                        if (status === "OK" && rendererRef.current) {
                            rendererRef.current.setDirections(result);
                        } else {
                            mapRef.current.setCenter({ lat: destination.lat, lng: destination.lng });
                            new google.maps.Marker({ position: destination, map: mapRef.current });
                        }
                    }
                );
            } else {
                new google.maps.Marker({ position: destination, map: mapRef.current });
            }
        }).catch(() => setError(true));
        return () => { cancelled = true; };
    }, [origin?.lat, origin?.lng, destination?.lat, destination?.lng]);

    if (error || !destination?.lat) {
        return (
            <div className={`flex items-center justify-center text-center text-xs text-muted-foreground bg-secondary/30 grid-noise ${className}`}
                 data-testid={`${testid}-fallback`}>
                <div className="space-y-1">
                    <MapPin className="w-5 h-5 mx-auto opacity-50" />
                    <div>Map unavailable</div>
                </div>
            </div>
        );
    }

    return <div ref={containerRef} className={className} data-testid={testid} />;
}
